/**
 * Сетка дронов: каждому устройству своя плитка со своей мини-картой.
 *
 * Отказ от одного общего фокуса. На общей карте, чтобы разглядеть один
 * аппарат, приходится приблизиться — и остальные девятнадцать пропадают из
 * виду. Здесь у каждого свой кадр, всегда центрированный на нём, и следить
 * можно за всеми сразу.
 *
 * Сверху — то, что требует вмешательства: нормальные состояния не должны
 * конкурировать за внимание с отклонениями.
 */

import './DroneGrid.css';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import DroneMiniMap from './DroneMiniMap';
import { ModemData, ModemPositionData } from '../../api/Drone';
import { HubTelemetryMessage } from '../../asp/DroneHub';
import { AttentionItem } from '../../hooks/useDroneTriage';
import { getModeName } from '../../utils/mavMode';

interface DroneGridProps {
    drones: ModemData[];
    positions: ModemPositionData[];
    telemetry: Map<string, HubTelemetryMessage>;
    attention: AttentionItem[];
    selectedImei: string | null;
    onSelect: (imei: string) => void;
}

// Array.isArray, а не `??`: сервер присылает в batteryRemaining не только
// массив, и без этой проверки .map роняет дерево React (error boundary нет).
function lowestBattery(drone: ModemData, telemetry: HubTelemetryMessage | undefined): number | null {
    const raw = telemetry?.batteryRemaining ?? drone.batteryRemaining;
    if (!Array.isArray(raw)) {
        return null;
    }
    const values = raw
        .map((b) => b?.remaining)
        .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0);

    return values.length > 0 ? Math.round(Math.min(...values)) : null;
}

function DroneGrid({ drones, positions, telemetry, attention, selectedImei, onSelect }: DroneGridProps): ReactNode {
    const { t } = useTranslation(['console']);

    const positionByImei = new Map(positions.map((p) => [p.imei, p]));

    return (
        <div className='drone-grid-wrap'>
            {attention.length > 0 &&
                <section className='drone-grid-attention'>
                    <h2 className='drone-grid-attention-title'>
                        {t('attention_title')} <span className='drone-grid-count'>{attention.length}</span>
                    </h2>
                    <ul className='drone-grid-attention-list'>
                        {attention.map((item) => (
                            <li key={item.imei}>
                                <button
                                    type='button'
                                    className={'drone-grid-attention-item drone-grid-attention-' + item.level}
                                    onClick={() => onSelect(item.imei)}
                                >
                                    <span className='drone-grid-attention-name'>{item.name}</span>
                                    <span className='drone-grid-attention-reason'>
                                        {t(item.reason, { value: item.value })}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            }

            <div className='drone-grid'>
                {drones.map((drone) => {
                    const pos = positionByImei.get(drone.imei);
                    const telem = telemetry.get(drone.imei);
                    const battery = lowestBattery(drone, telem);
                    const isSelected = drone.imei === selectedImei;

                    return (
                        <button
                            type='button'
                            key={drone.imei}
                            className={'drone-tile'
                                + (isSelected ? ' drone-tile-selected' : '')
                                + (drone.isOnline ? '' : ' drone-tile-offline')}
                            onClick={() => onSelect(drone.imei)}
                        >
                            <div className='drone-tile-head'>
                                <span className='drone-tile-name'>{drone.name}</span>
                                {drone.isArm &&
                                    <span className='drone-tile-arm'>ARM</span>
                                }
                            </div>

                            <DroneMiniMap
                                lat={pos?.lat ?? null}
                                lng={pos?.lng ?? null}
                                yaw={pos?.yaw ?? 0}
                                isOnline={drone.isOnline}
                                mavType={drone.mavType}
                                noPositionLabel={t('tile_no_position')}
                            />

                            <div className='drone-tile-foot'>
                                <span className='drone-tile-mode'>
                                    {getModeName(drone.mavType, drone.mode, drone.isOnline)}
                                </span>
                                <span className='drone-tile-stats'>
                                    {battery !== null &&
                                        <span className={'drone-tile-battery'
                                            + (battery <= 15 ? ' drone-tile-battery-critical' : '')
                                            + (battery > 15 && battery <= 25 ? ' drone-tile-battery-low' : '')}>
                                            {battery}%
                                        </span>
                                    }
                                    {telem &&
                                        <span className='drone-tile-sats'>{t('tile_sats', { count: telem.sputniksCount })}</span>
                                    }
                                    {telem && drone.isArm &&
                                        <span className='drone-tile-alt'>{t('tile_alt', { value: Math.round(telem.height) })}</span>
                                    }
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default DroneGrid;
